import { useEffect, useSyncExternalStore } from 'react';

import type { AppCapability, NativeToWebPayloads } from '@sunflower-games/webview-bridge';
import { getAppInfo, isBridgeAvailable } from '@sunflower-games/webview-bridge';

import { logToNewRelic } from '../debug';

import { getShellBridge } from './shellBridge';

export type ShellInfo = NativeToWebPayloads['app.info'];

/**
 * - `pending`: a bridge global is on the page and the shell has not answered `app.getInfo` yet.
 * - `confirmed`: the shell answered — this page runs inside the mobile shell.
 * - `absent`: no bridge global, a refused origin, or no answer in time.
 */
export type ShellPresence = 'pending' | 'confirmed' | 'absent';

/**
 * Long enough for a cold shell to answer from its bootstrap payload, short enough that a page which
 * only looks like a shell does not hold its visitor on a loader.
 */
export const SHELL_CONFIRM_TIMEOUT_MS = 3000;

let presence: ShellPresence | null = null;
let info: ShellInfo | null = null;
let confirmation: Promise<ShellPresence> | null = null;
const listeners = new Set<() => void>();

const settle = (next: ShellPresence, nextInfo: ShellInfo | null = null) => {
  presence = next;
  info = nextInfo;
  listeners.forEach((listener) => listener());
};

/**
 * Asks the shell to identify itself, once per page. The bridge global alone is not proof: it only says
 * something injected `__CROWN_BRIDGE__` — the shell answering `app.getInfo` is. Never rejects.
 */
export function confirmShell(): Promise<ShellPresence> {
  if (confirmation) return confirmation;

  const bridge = getShellBridge();
  if (bridge === null) {
    // Not memoized, and not settled: the shell's before-content injection is not ordered against this
    // page's scripts (see `shellBridge.ts`), so a global that is missing now can still arrive. The next
    // call — and `getShellPresence`, which reads the global live until something settles — checks again.
    return Promise.resolve<ShellPresence>('absent');
  }

  // The shell's bootstrap script installs the same `app.info` it answers with, so a shell that has it
  // is confirmed with no round trip and no loader. The request covers an older shell without it.
  const seeded = getAppInfo();
  if (seeded !== null) {
    settle('confirmed', seeded);
    confirmation = Promise.resolve<ShellPresence>('confirmed');
    return confirmation;
  }

  presence = 'pending';
  confirmation = bridge.request('app.getInfo', {}, 'app.info', SHELL_CONFIRM_TIMEOUT_MS).then(
    (reply) => {
      settle('confirmed', reply);
      return 'confirmed' as const;
    },
    (error: unknown) => {
      // Sticky for the page's life, so worth knowing how often a real shell lands here.
      logToNewRelic('Shell presence: no app.getInfo answer', { reason: String(error) }, 'warn');
      settle('absent');
      return 'absent' as const;
    },
  );

  return confirmation;
}

export function getShellPresence(): ShellPresence {
  if (presence) return presence;
  if (!isBridgeAvailable()) return 'absent';
  return getAppInfo() !== null ? 'confirmed' : 'pending';
}

/**
 * Whether this page runs inside the mobile shell — the one predicate for every "native or web?" branch
 * (sign-in, the conflict dialogs, Google's in-app block, payments). The bridge global alone is not
 * enough: it stays installed on a refused origin, and a shell that never answers would leave a native
 * request hanging.
 */
export function isInShell(): boolean {
  return getShellPresence() === 'confirmed';
}

/** The shell's `app.info`; `null` until it is `confirmed`. */
export function getShellInfo(): ShellInfo | null {
  return info ?? (isBridgeAvailable() ? getAppInfo() : null);
}

/** Whether the shell announces `capability` in its `app.info` — the one capability check for every shell feature. */
export function hasShellCapability(capability: AppCapability, shellInfo: ShellInfo | null = getShellInfo()): boolean {
  return shellInfo?.capabilities?.includes(capability) === true;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Starts the confirmation on first use and re-renders when it settles. */
export function useShellPresence(): ShellPresence {
  const current = useSyncExternalStore(subscribe, getShellPresence);

  useEffect(() => {
    void confirmShell();
  }, []);

  return current;
}

/**
 * The shell's `app.info`, re-rendering when the confirmation settles. `null` in a plain browser. Reads the
 * settled answer only — never the injected global directly — so the snapshot is a stable reference.
 */
export function useShellInfo(): ShellInfo | null {
  const current = useSyncExternalStore(subscribe, () => info);

  useEffect(() => {
    void confirmShell();
  }, []);

  return current;
}

/**
 * The installed app's version as the native app's settings show it — `1.4.0 (52)` — with the OTA code push
 * appended when the shell reports one, `1.4.0 (52.3)`, the same as `rips-ui`'s settings. `otaBuild` is for
 * people to read, never to key on.
 */
export function formatShellVersion({ appVersion, buildNumber, otaBuild }: ShellInfo): string {
  return otaBuild === undefined ? `${appVersion} (${buildNumber})` : `${appVersion} (${buildNumber}.${otaBuild})`;
}

/** Test seam: forget the answer so the next {@link confirmShell} asks again. */
export function resetShellPresence(): void {
  presence = null;
  info = null;
  confirmation = null;
}
